import './service-product-modal.css';
import StartupPageTitle from './StartupPageTitle';
/**
 * MarketplaceShell.jsx — unified founder-facing marketplace used by Services,
 * Credit, and Marketing Agencies.  Same layout, same behaviour, accent + copy
 * differ by `category`.
 *
 * Top:   marketplace hero with title + intro + live counts.
 * Below: two tabs — "Browse by Vendor" / "Browse by Product".
 *   Vendor tab  → grid of vendor cards (rich profile click-through).
 *   Product tab → grid of catalog items, click opens drawer with all vendors
 *                 offering it, sortable by price / rating.
 *
 * Services is product-only (Founderboard owns the delivery, so founders pick an
 * outcome rather than a provider) — the vendor tab is hidden there and the
 * toggle collapses to nothing since there's no longer a choice to make.
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { fileUrl } from '@/api';
import { useCurrency } from '@/hooks/useCurrency';
import { toast } from 'sonner';
import {
  Buildings, Bank, Megaphone, ArrowRight, MagnifyingGlass, Check, X,
  PaperPlaneTilt, Package, Storefront, Funnel, FileText, CaretDown,
  ShieldCheck, Warning, Info, Handshake, CurrencyCircleDollar,
} from '@phosphor-icons/react';
import RequiredDocsView from './RequiredDocsView';
import ActiveEngagementsBanner from './ActiveEngagementsBanner';
import useServiceCardScroll from './useServiceCardScroll';
import ServiceMarketplaceCatalog, { ServiceMarketplaceSummary } from './ServiceMarketplaceCatalog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONFIG = {
  service: {
    accent: '#0d9488',
    Icon: Buildings,
    title: 'Startup Services',
    intro: 'LLP / GST / IP / compliance / hiring & more — vetted partners with transparent pricing.',
    vendorProfilePath: (id) => `/startup/services/${id}`,
    introEndpoint: 'vendor-intros',
    basePath: '/startup/services',
  },
  credit: {
    accent: '#f59e0b',
    Icon: Bank,
    title: 'Startup Credit',
    intro: 'Corporate cards, working-capital loans, bill discounting & more — surface what fits your stage.',
    vendorProfilePath: (id) => `/startup/credit/${id}`,
    introEndpoint: 'vendor-intros',
    basePath: '/startup/credit',
  },
  agency: {
    accent: '#6366f1',
    Icon: Megaphone,
    title: 'Marketing Agencies',
    intro: 'Design, performance, content, video, PR — partner with agencies who get startups.',
    vendorProfilePath: (id) => `/startup/marketing/agency/${id}`,
    introEndpoint: 'intro-calls',
    basePath: '/startup/marketing',
  },
};

const cadenceLabel = (c) => ({
  one_time: 'one-time', monthly: '/ month', quarterly: '/ quarter', annual: '/ year',
}[c] || c);

const MarketplaceShell = ({ category }) => {
  const cardScrollRef = useServiceCardScroll(category === 'service');
  const cfg = CONFIG[category];
  const { accent, Icon, title, intro } = cfg;
  const navigate = useNavigate();
  const vendorBrowsing = category !== 'service';
  const [tab, setTab] = useState(vendorBrowsing ? 'vendor' : 'product');   // 'vendor' | 'product'
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reload, setReload] = useState(0);
  const [search, setSearch] = useState('');
  const [productDrawer, setProductDrawer] = useState(null);   // catalog item id
  const [introFor, setIntroFor] = useState(null);
  const [lockedCount, setLockedCount] = useState(0);          // # of unpaid service engagements
  const [showLockedModal, setShowLockedModal] = useState(false);
  const [myEngagements, setMyEngagements] = useState([]);      // this category's engagements, for the banner

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [v, p] = await Promise.all([
          api.get(`/marketplace/${category}/by-vendor`),
          api.get(`/marketplace/${category}/by-product`),
        ]);
        if (!cancelled) {
          setVendors(v.data.vendors || []);
          setItems(p.data.items || []);
        }
      } catch (e) {
        if (!cancelled) {
          const message = e.response?.data?.detail || 'Please try again in a moment.';
          if (category === 'service') setLoadError(message);
          else toast.error(e.response?.data?.detail || 'Failed to load marketplace');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, reload]);

  // The founder's own engagements in this marketplace. Feeds two things:
  //   * (Iter 66) the rotating in-flight banner, on every category;
  //   * (Iter 61) the services-only "pay to begin engagement" popup + warning
  //     banner, which stays gated to `service` because only services block a
  //     vendor's document access on payment.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/marketplace-engagements/mine');
        const mine = (r.data.engagements || []).filter((e) => e.vendor_type === category);
        if (cancelled) return;
        setMyEngagements(mine);
        if (category !== 'service') return;
        const locked = mine.filter((e) => e.is_locked);
        setLockedCount(locked.length);
        if (locked.length > 0) {
          const seenKey = 'fb_services_locked_popup_seen';
          if (!sessionStorage.getItem(seenKey)) {
            setShowLockedModal(true);
            sessionStorage.setItem(seenKey, '1');
          }
        }
      } catch (_) { /* silent — banners just won't show */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const filteredVendors = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vendors;
    return vendors.filter((v) =>
      v.name?.toLowerCase().includes(q) ||
      v.tagline?.toLowerCase().includes(q) ||
      (v.catalog_offerings || []).some((o) => (o.catalog_item_name || '').toLowerCase().includes(q))
    );
  }, [vendors, search]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.name?.toLowerCase().includes(q) || (it.description || '').toLowerCase().includes(q));
  }, [items, search]);

  const totalOfferings = vendors.reduce((s, v) => s + (v.catalog_offerings || []).length, 0);

  return (
    <div ref={cardScrollRef} className={category === 'service' ? 'services-marketplace' : 'p-6'} data-testid={`marketplace-shell-${category}`}>
      <div className={category === 'service' ? 'services-content' : 'max-w-6xl mx-auto'}>
        {category === 'service' && <ServiceMarketplaceSummary vendors={vendors} items={items} totalOfferings={totalOfferings} loading={loading} />}
        {/* (Iter 66) Rotating ticker of whatever the founder already has in flight here. */}
        {category !== 'service' && <ActiveEngagementsBanner
          engagements={myEngagements}
          category={category}
          accent={accent}
          basePath={cfg.basePath}
        />}
        {/* (Iter 61) Persistent banner: unpaid service engagements block vendor access to founder docs */}
        {category === 'service' && lockedCount > 0 && (
          <div
            className="mb-4 rounded-xl border border-warning/40 bg-warning/10 p-3.5 flex items-center gap-3 flex-wrap"
            data-testid="services-locked-banner"
          >
            <Warning size={16} weight="fill" className="text-warning flex-shrink-0" />
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm font-semibold text-text-primary">
                {lockedCount} unpaid engagement{lockedCount === 1 ? '' : 's'} — complete payment to begin
              </div>
              <div className="text-[11px] text-text-muted">
                Your service vendor{lockedCount === 1 ? '' : 's'} can&rsquo;t access your documents or start work until you pay the upfront charge.
              </div>
            </div>
            <button
              onClick={() => navigate('/startup/billing')}
              data-testid="services-locked-banner-cta"
              className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white inline-flex items-center gap-1"
              style={{ backgroundColor: accent }}
            >
              Go to Billing <ArrowRight size={10} weight="bold" />
            </button>
          </div>
        )}
        {category === 'service' ? <ServiceMarketplaceCatalog items={items} loading={loading} error={loadError} onRetry={() => setReload(n => n + 1)} onPick={setProductDrawer} /> : <>
        {/* HERO */}
        <div className="bg-surface border border-border rounded-2xl p-6 md:p-7 mb-6 startup-page-header" data-testid={`marketplace-${category}-hero`}>
          <div className="flex items-start gap-4 flex-wrap">
            <span className="w-11 h-11 rounded-xl inline-flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accent}1a`, color: accent }}>
              <Icon size={20} weight="fill" />
            </span>
            <div className="flex-1 min-w-[260px]">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: accent }}>Marketplace</div>
              <StartupPageTitle icon={Icon} className="text-3xl font-heading font-bold text-text-primary leading-[1.05]" style={{ letterSpacing: '-0.02em' }}>{title}</StartupPageTitle>
              <p className="text-sm text-text-secondary mt-1.5 max-w-2xl">{intro}</p>
            </div>
            <div className="flex gap-4 text-right">
              <Stat label="Vendors" value={vendors.length} accent={accent} testid={`marketplace-${category}-stat-vendors`} />
              <Stat label="Products" value={items.length} accent={accent} testid={`marketplace-${category}-stat-products`} />
              <Stat label="Live offerings" value={totalOfferings} accent={accent} testid={`marketplace-${category}-stat-offerings`} />
            </div>
          </div>
        </div>

        {/* TAB TOGGLE + SEARCH */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          {vendorBrowsing && (
            <div className="inline-flex rounded-lg border border-border p-0.5 bg-surface" role="tablist">
              <TabBtn active={tab === 'vendor'} onClick={() => setTab('vendor')} accent={accent} icon={Storefront} testid={`marketplace-${category}-tab-vendor`}>Browse by Vendor</TabBtn>
              <TabBtn active={tab === 'product'} onClick={() => setTab('product')} accent={accent} icon={Package} testid={`marketplace-${category}-tab-product`}>Browse by Product</TabBtn>
            </div>
          )}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <MagnifyingGlass size={12} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tab === 'vendor' ? 'Search vendors…' : 'Search products…'} data-testid={`marketplace-${category}-search`} className="w-full pl-8 pr-3 py-2 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-text-primary" />
          </div>
        </div>

        {/* BODY */}
        {loading ? (
          <div className="text-sm text-text-muted py-12 text-center">Loading marketplace…</div>
        ) : vendorBrowsing && tab === 'vendor' ? (
          <VendorGrid vendors={filteredVendors} cfg={cfg} onIntro={setIntroFor} />
        ) : (
          <ProductGrid items={filteredItems} accent={accent} onPick={setProductDrawer} />
        )}
        </>}
      </div>

      {productDrawer && (
        <ProductDrawer category={category} cfg={cfg} itemId={productDrawer} onClose={() => setProductDrawer(null)} onIntro={(v) => { setProductDrawer(null); setIntroFor(v); }} />
      )}
      {introFor && (
        <IntroModal category={category} cfg={cfg} vendor={introFor} onClose={() => setIntroFor(null)} />
      )}
      {showLockedModal && category === 'service' && (
        <LockedEngagementsModal
          count={lockedCount}
          accent={accent}
          onClose={() => setShowLockedModal(false)}
          onGoToBilling={() => { setShowLockedModal(false); navigate('/startup/billing'); }}
        />
      )}
    </div>
  );
};

const LockedEngagementsModal = ({ count, accent, onClose, onGoToBilling }) => (
  <div
    className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    role="dialog"
    aria-modal="true"
    data-testid="services-locked-modal"
    onClick={onClose}
  >
    <div
      className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start gap-3 mb-4">
        <span
          className="w-11 h-11 rounded-xl inline-flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${accent}1a`, color: accent }}
        >
          <Warning size={18} weight="fill" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-heading font-bold text-text-primary" style={{ letterSpacing: '-0.01em' }}>
            Complete payment to begin
          </h3>
          <p className="text-xs text-text-muted mt-0.5">Service engagement{count === 1 ? '' : 's'} awaiting your upfront payment</p>
        </div>
        <button onClick={onClose} data-testid="services-locked-modal-close" className="text-text-muted hover:text-text-primary">
          <X size={16} weight="bold" />
        </button>
      </div>
      <p className="text-sm text-text-secondary leading-relaxed">
        You have <strong className="text-text-primary">{count} unpaid engagement{count === 1 ? '' : 's'}</strong>.
        The vendor{count === 1 ? '' : 's'} can only access your documents and start work once the upfront charge is paid.
      </p>
      <div className="mt-5 flex items-center justify-end gap-2">
        <button
          onClick={onClose}
          data-testid="services-locked-modal-later"
          className="text-xs font-semibold px-3 py-2 rounded-lg border border-border text-text-secondary hover:text-text-primary"
        >
          Remind me later
        </button>
        <button
          onClick={onGoToBilling}
          data-testid="services-locked-modal-cta"
          className="text-xs font-semibold px-3.5 py-2 rounded-lg text-white inline-flex items-center gap-1.5"
          style={{ backgroundColor: accent }}
        >
          Make payment <ArrowRight size={10} weight="bold" />
        </button>
      </div>
    </div>
  </div>
);

const Stat = ({ label, value, accent, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">{label}</div>
    <div className="text-xl font-mono font-bold" style={{ color: accent }}>{value}</div>
  </div>
);

const TabBtn = ({ active, onClick, accent, icon: I, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`relative px-3.5 py-2 text-xs font-semibold inline-flex items-center gap-1.5 rounded-md transition-colors ${active ? 'text-white' : 'text-text-secondary hover:text-text-primary'}`} style={active ? { backgroundColor: accent } : {}}>
    <I size={11} weight="bold" /> {children}
  </button>
);

const VendorLogo = ({ logoFileId, name, accent, size = 36 }) => {
  // (Iter 51) Vendor logo or initials fallback. Public images endpoint serves
  // without auth so the card stays cheap to render.
  const [errored, setErrored] = useState(false);
  const initials = (name || '?').split(/\s+/).slice(0, 2).map((s) => s[0]).join('').toUpperCase();
  if (logoFileId && !errored) {
    return (
      <img
        src={`${API}/files/${logoFileId}/public`}
        alt={name}
        onError={() => setErrored(true)}
        style={{ width: size, height: size, borderRadius: 8, objectFit: 'cover' }}
        className="border border-border bg-white flex-shrink-0"
      />
    );
  }
  return (
    <span
      className="font-heading font-bold flex-shrink-0 inline-flex items-center justify-center"
      style={{ width: size, height: size, borderRadius: 8, background: `${accent}1a`, color: accent, fontSize: size * 0.38 }}
    >{initials || '·'}</span>
  );
};

const VendorGrid = ({ vendors, cfg, onIntro }) => {
  const { format } = useCurrency();
  const navigate = useNavigate();
  if (vendors.length === 0) return <Empty message="No vendors match yet." />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="marketplace-vendor-grid">
      {vendors.map((v) => (
        <div key={v.id} role="button" tabIndex={0}
          onClick={() => navigate(cfg.vendorProfilePath(v.id))}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate(cfg.vendorProfilePath(v.id)); }}
          data-testid={`marketplace-vendor-card-${v.id}`}
          className="bg-surface border border-border rounded-xl p-4 hover:border-text-primary transition-colors cursor-pointer flex flex-col">
          <div className="flex items-start gap-3 mb-2">
            <VendorLogo logoFileId={v.logo_file_id} name={v.name} accent={cfg.accent} />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-heading font-bold text-text-primary flex-1 truncate">{v.name}</h3>
                <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-1 flex-shrink-0" style={{ backgroundColor: `${cfg.accent}1a`, color: cfg.accent }}>
                  <Check size={9} weight="bold" /> Verified
                </span>
              </div>
              <p className="text-xs text-text-secondary mt-1 line-clamp-2">{v.tagline}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1 mb-3">
            {(v.catalog_offerings || []).slice(0, 3).map((o, i) => (
              <span key={i} className="text-[10px] bg-background border border-border rounded px-1.5 py-0.5">{o.catalog_item_name}</span>
            ))}
            {(v.catalog_offerings || []).length > 3 && <span className="text-[10px] text-text-muted">+{v.catalog_offerings.length - 3} more</span>}
          </div>
          <div className="flex items-center justify-between gap-2 mt-auto">
            <span className="text-[11px] text-text-muted">{(v.catalog_offerings || []).length} offering{(v.catalog_offerings || []).length === 1 ? '' : 's'}</span>
            <button onClick={(e) => { e.stopPropagation(); onIntro(v); }}
              data-testid={`marketplace-vendor-intro-${v.id}`}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg text-white hover:opacity-90"
              style={{ backgroundColor: cfg.accent }}>
              Request intro <ArrowRight size={9} weight="bold" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const ProductGrid = ({ items, accent, onPick }) => {
  const { format } = useCurrency();
  if (items.length === 0) return <Empty message="No products match yet." />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="marketplace-product-grid">
      {items.map((it) => {
        const hasVendors = it.vendor_count > 0;
        const isCredit = it.category === 'credit';
        // (Iter 65) Services advertise the all-in figure (service charge + govt
        // fees) — that's the number the founder actually pays.
        const isService = it.category === 'service';
        const min = isService && it.min_total != null ? it.min_total : it.min_price;
        const cur = it.currency || 'INR';
        const hasGovtFees = isService && it.min_total != null && it.min_total > (it.min_price || 0);
        return (
          <button key={it.id} onClick={() => onPick(it.id)} disabled={!hasVendors}
            data-testid={`marketplace-product-card-${it.id}`}
            className={`text-left bg-surface border border-border rounded-xl p-4 transition-colors flex flex-col ${hasVendors ? 'hover:border-text-primary cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}>
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-sm font-heading font-bold text-text-primary flex-1 leading-snug">{it.name}</h3>
            </div>
            <p className="text-xs text-text-secondary mb-3 line-clamp-2 flex-1">{it.description}</p>
            <div className="flex items-end justify-between gap-2 mt-auto">
              {isCredit ? (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">{hasVendors ? 'Available from' : 'Coming soon'}</div>
                  <div className="font-mono font-bold text-sm" style={{ color: hasVendors ? accent : 'var(--text-muted)' }}>
                    {hasVendors ? `${it.vendor_count} lender${it.vendor_count === 1 ? '' : 's'}` : '—'}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">{hasVendors ? 'Starting at' : 'Coming soon'}</div>
                  <div className="font-mono font-bold text-sm" style={{ color: hasVendors ? accent : 'var(--text-muted)' }}>
                    {hasVendors ? format(min || 0, cur) : '—'}
                  </div>
                  {hasVendors && hasGovtFees && (
                    <div className="text-[9px] text-text-muted">incl. govt fees</div>
                  )}
                </div>
              )}
              <div className="text-[10px] text-text-muted text-right">
                {!isCredit && <div>{it.vendor_count} vendor{it.vendor_count === 1 ? '' : 's'}</div>}
                {hasVendors && <div className="inline-flex items-center gap-0.5 text-text-secondary mt-0.5">Compare <ArrowRight size={8} weight="bold" /></div>}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

const Empty = ({ message }) => (
  <div className="border border-dashed border-border rounded-xl p-12 text-center text-sm text-text-muted bg-surface">{message}</div>
);

const ServicePartnerLogo = ({ vendor }) => {
  const [failed, setFailed] = useState(false);
  const logo = vendor.vendor_logo_file_id || vendor.vendor_logo_url;
  useEffect(() => setFailed(false), [logo]);
  return <div className="service-partner-logo" aria-hidden="true">
    {logo && !failed ? <img src={fileUrl(logo)} alt="" onError={() => setFailed(true)} />
      : <span>{(vendor.vendor_name || 'Partner').trim().slice(0,2).toUpperCase()}</span>}
  </div>;
};

const ProductDrawer = ({ category, cfg, itemId, onClose, onIntro }) => {
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [taxonomy, setTaxonomy] = useState([]);
  const [filledSlots, setFilledSlots] = useState(new Set());
  const [sort, setSort] = useState('price');   // 'price' | 'rating'
  const [expandedVendor, setExpandedVendor] = useState(null);
  // (Iter 65) Services don't "request an intro" — picking a vendor here opens a
  // guided request (agreement → documents → quote → payment).
  const isServiceFlow = category === 'service';
  const [starting, setStarting] = useState('');
  const vendorTrackRef = useRef(null);
  const [vendorNavigation, setVendorNavigation] = useState({ previous: false, next: false });
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!isServiceFlow) return undefined;
    const previous = document.activeElement;
    const main = previous?.closest('main');
    const overflow = main?.style.overflow;
    if (main) main.style.overflow = 'hidden';
    const timer = window.setTimeout(() => dialogRef.current?.focus(), 0);
    const keydown = event => {
      if (event.key === 'Escape') { event.preventDefault(); closeRef.current(); }
      if (event.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const items = [...dialog.querySelectorAll('button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),[tabindex="0"]')].filter(el => el.offsetParent !== null);
      if (!items.length) { event.preventDefault(); dialog.focus(); return; }
      const first=items[0],last=items[items.length-1];
      if (!dialog.contains(document.activeElement) || document.activeElement===dialog) { event.preventDefault(); (event.shiftKey ? last : first).focus(); }
      else if (event.shiftKey && document.activeElement===first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement===last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown',keydown);
    return () => { window.clearTimeout(timer); document.removeEventListener('keydown',keydown); if(main) main.style.overflow=overflow; if(previous?.isConnected) previous.focus(); };
  }, [isServiceFlow, !!data]);


  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [drawer, tax, vault] = await Promise.all([
          api.get(`/marketplace/${category}/product/${itemId}`),
          api.get('/vault/structure'),
          api.get('/vault').catch(() => ({ data: { files: [] } })),
        ]);
        if (!cancelled) {
          setData(drawer.data);
          setTaxonomy(tax.data.datarooms || []);
          setFilledSlots(new Set((vault.data.files || []).map((f) => `${f.dataroom_id}::${f.slot_id}`)));
        }
      } catch (e) { toast.error('Failed to load'); onClose(); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const sortedVendors = useMemo(() => {
    if (!data) return [];
    const arr = [...(data.vendors || [])];
    // Sort services on the all-in figure — ranking on the service charge alone
    // would put a cheap-fee/expensive-filing vendor above a genuinely cheaper one.
    if (sort === 'price') arr.sort((a, b) => (a.total_price ?? a.gross_price ?? 0) - (b.total_price ?? b.gross_price ?? 0));
    if (sort === 'rating') arr.sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0));
    return arr;
  }, [data, sort]);

  useEffect(() => {
    if (!isServiceFlow || !data) return undefined;
    const track = vendorTrackRef.current;
    if (!track) return undefined;
    const update = () => setVendorNavigation({ previous: track.scrollLeft > 2, next: track.scrollLeft + track.clientWidth < track.scrollWidth - 2 });
    track.scrollLeft = 0;
    update();
    const observer = new ResizeObserver(update);
    observer.observe(track);
    track.addEventListener('scroll', update, { passive: true });
    return () => { observer.disconnect(); track.removeEventListener('scroll', update); };
  }, [isServiceFlow, data, sort]);

  const moveVendors = direction => {
    const track = vendorTrackRef.current;
    if (!track) return;
    const card = track.querySelector('.compare-card');
    const distance = card ? card.getBoundingClientRect().width + 16 : track.clientWidth;
    track.scrollBy({ left: direction * distance, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };

  // Step 1 → 2: mint (or resume) the request, then hand off to the wizard.
  const startRequest = async (vendorId) => {
    setStarting(vendorId);
    try {
      const r = await api.post('/service-requests', {
        vendor_id: vendorId,
        catalog_item_id: data.item.id,
      });
      const req = r.data.request;
      if (r.data.resumed) toast.info('Picking up your existing request for this service.');
      navigate(`/startup/services/request/${req.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start this request');
      setStarting('');
    }
  };

  if (!data) return <div className={isServiceFlow ? 'fb-service-modal' : 'fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-center justify-center'} onClick={onClose}><div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Loading service details" className="service-modal-loading" onClick={event=>event.stopPropagation()}><span role="status">Loading service and partners…</span><button type="button" onClick={onClose} aria-label="Close service details"><X size={18} /></button></div></div>;

  if (isServiceFlow) return (
    <div className="fb-service-modal service-compare" onClick={onClose} data-testid="marketplace-product-drawer">
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="service-product-title" tabIndex={-1} className="service-compare-panel" onClick={e => e.stopPropagation()}>
        <header className="compare-header">
          <div className="compare-title"><span className="compare-eyebrow"><Package size={15} weight="duotone" /> FOUNDERBOARD SERVICES</span><h3 id="service-product-title">{data.item.name}</h3></div>
          <button type="button" className="compare-close" onClick={onClose} aria-label="Close service details"><X size={18} weight="bold" /></button>
        </header>
        <div className="compare-toolbar">
          <div><strong>Find your partner</strong><span>{sortedVendors.length} available {sortedVendors.length === 1 ? 'partner' : 'partners'}</span></div>
          <div className="compare-toolbar-controls"><div className="compare-sort-options" role="group" aria-label="Sort partners"><button type="button" onClick={() => setSort('price')} aria-pressed={sort === 'price'} data-testid="drawer-sort-price"><CurrencyCircleDollar size={15} weight="duotone" />Lowest total price</button><button type="button" onClick={() => setSort('rating')} aria-pressed={sort === 'rating'} data-testid="drawer-sort-rating"><span aria-hidden="true">★</span>Highest rating</button></div></div>
        </div>
        <div className="compare-body">
          <div className="compare-carousel-shell">
            {sortedVendors.length > 0 && <button type="button" className="compare-carousel-arrow compare-carousel-previous" onClick={() => moveVendors(-1)} disabled={!vendorNavigation.previous} aria-label="Previous partner" aria-controls="service-partner-carousel"><ArrowRight size={20} weight="bold" style={{transform:'rotate(180deg)'}} /></button>}
          {sortedVendors.length === 0 ? <Empty message="No partners are offering this service yet." /> : <div className="compare-carousel-viewport" ref={vendorTrackRef} id="service-partner-carousel" role="region" aria-label="Available service partners"><div className="compare-grid">
            {sortedVendors.map(v => {
              const docs = v.required_documents || [];
              const ready = docs.filter(d => filledSlots.has(`${d.dataroom_id}::${d.slot_id}`)).length;
              const expanded = expandedVendor === v.vendor_id;
              const days = v.delivery_days || v.turnaround_days;
              return <article className="compare-card" key={v.vendor_id} data-testid={`drawer-vendor-row-${v.vendor_id}`}>
                <div className="compare-partner"><ServicePartnerLogo vendor={v} /><div><h4>{v.vendor_name}</h4><span className="compare-rating" data-testid={`drawer-rating-${v.vendor_id}`}>{v.rating_avg != null && v.review_count > 0 ? <><span className="compare-star">★</span> {Number(v.rating_avg).toFixed(1)} <span>· {v.review_count} {v.review_count === 1 ? 'review' : 'reviews'}</span></> : 'No reviews yet'}</span></div></div>
                <p className="compare-tagline">{v.vendor_tagline || 'Service partner on FounderBoard.'}</p>
                <div className="compare-pricing"><span className="compare-price-label">Listed total</span><strong data-testid={`drawer-total-${v.vendor_id}`}>{format(v.total_price ?? v.gross_price ?? 0, v.currency || 'INR')}</strong><span>{cadenceLabel(v.cadence)}</span></div>
                <dl className="compare-facts"><div><dt>Service charge</dt><dd>{format(v.gross_price || 0, v.currency || 'INR')}</dd></div>{(v.govt_fees || 0) > 0 && <div><dt>Government fees</dt><dd>{format(v.govt_fees, v.currency || 'INR')}</dd></div>}<div><dt>Delivery estimate</dt><dd>{days ? `${days} days` : 'Confirm with partner'}</dd></div></dl>
                <div className="compare-documents">
                  {docs.length > 0 ? <><button type="button" className="compare-docs-button" onClick={() => setExpandedVendor(expanded ? null : v.vendor_id)} aria-expanded={expanded} aria-controls={`service-docs-${v.vendor_id}`} data-testid={`drawer-docs-toggle-${v.vendor_id}`}><FileText size={17} weight="duotone" /><span><strong>{docs.length} {docs.length === 1 ? 'document' : 'documents'} required</strong><small>{ready} of {docs.length} ready in your vault</small></span><CaretDown size={13} style={{transform: expanded ? 'rotate(180deg)' : 'none'}} /></button>{expanded && <div className="compare-docs-panel" id={`service-docs-${v.vendor_id}`} data-testid={`drawer-docs-panel-${v.vendor_id}`}><RequiredDocsView items={docs} filledSlots={filledSlots} taxonomy={taxonomy} accent="#0d9488" /></div>}</> : <div className="compare-no-docs"><FileText size={17} weight="duotone" /> No documents listed</div>}
                </div>
                <button type="button" className="compare-choose" onClick={() => startRequest(v.vendor_id)} disabled={!!starting} data-testid={`drawer-select-${v.vendor_id}`}>{starting === v.vendor_id ? 'Starting…' : 'Choose partner'}<ArrowRight size={16} weight="bold" /></button>
                <div className="compare-card-fee-slot">{Number(v.govt_fees) > 0 && <p className="compare-card-fee-note"><Info size={12} aria-hidden="true" /><span>Government fees are indicative and may vary by state.</span></p>}</div>
              </article>;
            })}
          </div></div>}
            {sortedVendors.length > 0 && <button type="button" className="compare-carousel-arrow compare-carousel-next" onClick={() => moveVendors(1)} disabled={!vendorNavigation.next} aria-label="Next partner — browse more vendors" aria-controls="service-partner-carousel"><ArrowRight size={20} weight="bold" /></button>}
          </div>
        </div>
        <footer className="compare-footer footer-process" data-testid="drawer-service-flow-note">
          <ol className="footer-process-track" aria-label="Service request process">
            {[{label:'Agreement',icon:Handshake},{label:'Documents',icon:FileText},{label:'Final quote',icon:Package},{label:'Payment',icon:CurrencyCircleDollar}].map(({label,icon:Icon},index) => <li className="footer-process-step" key={label} style={{'--step-index':index}}><span className="footer-process-icon"><Icon size={20} weight="duotone" aria-hidden="true" /></span><span className="footer-process-label"><small>STEP {index+1}</small><strong>{label}</strong></span>{index < 3 && <span className="footer-process-connector" aria-hidden="true"><i /></span>}</li>)}
          </ol>
          <div className="footer-approval-reveal"><span className="footer-approval-badge"><ShieldCheck size={25} weight="duotone" aria-hidden="true" /></span><div><strong>You approve the final quote <em>before payment.</em></strong><p>Review the final quote before you proceed to payment.</p></div></div>
        </footer>
      </section>
    </div>
  );

  return (
    <div className={isServiceFlow ? "fb-service-modal" : "fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-start justify-center pt-12 p-4 overflow-y-auto"} onClick={onClose} data-testid="marketplace-product-drawer">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="service-product-title" tabIndex={-1} className="service-modal-panel bg-background border border-border rounded-2xl p-6 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="service-modal-header flex justify-between items-start mb-4">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: cfg.accent }}>{isServiceFlow ? "FOUNDERBOARD SERVICES" : "Product"}</div>
            <h3 id="service-product-title" className="font-heading font-bold text-xl mb-1.5" style={{ letterSpacing: '-0.01em' }}>{data.item.name}</h3>
            {!isServiceFlow && <p className="text-sm text-text-secondary">{data.item.description}</p>}
          </div>
          <button type="button" aria-label="Close service details" onClick={onClose} className="service-modal-close text-text-muted hover:text-text-primary"><X size={16} weight="bold" /></button>
        </div>

        {isServiceFlow && (
          <div className="mt-4 rounded-xl p-3 text-[11px] leading-relaxed"
            style={{ backgroundColor: `${cfg.accent}12`, border: `1px solid ${cfg.accent}44` }}
            data-testid="drawer-service-flow-note">
            <div className="service-flow-heading"><ShieldCheck size={18} weight="bold" /><strong>Choose a partner. Approve the quote before you pay.</strong></div>
            <div className="service-flow-steps" aria-label="Service request steps">
              {[
                {label:'Agreement', icon:Handshake}, {label:'Documents', icon:FileText},
                {label:'Final quote', icon:Package}, {label:'Payment', icon:CurrencyCircleDollar},
              ].map(({label,icon:StepIcon},index) => <div className="service-flow-step" key={label} style={{'--step-index':index}}>
                <span className="service-flow-step-icon"><StepIcon size={17} weight="duotone" /><small>{String(index+1).padStart(2,'0')}</small></span>
                <span className="service-flow-step-label">{label}</span>
                {index<3 && <span className="service-flow-connector" aria-hidden="true"><i /></span>}
              </div>)}
            </div>
            <p>Your partner receives documents and contact details only after payment.</p>
          </div>
        )}

        <div className="service-modal-toolbar flex items-center justify-between mt-5 mb-3">
          <div className="text-[11px] uppercase tracking-wider font-bold text-text-muted">
            {isServiceFlow ? `Available partners (${sortedVendors.length})` : `Vendors offering this (${sortedVendors.length})`}
          </div>
          <div className="inline-flex gap-1 text-[11px]">
            <span className="text-text-muted inline-flex items-center gap-1 mr-1"><Funnel size={10} weight="bold" /> Sort:</span>
            <button onClick={() => setSort('price')} aria-pressed={sort==='price'} data-testid="drawer-sort-price" className={`px-2 py-1 rounded font-semibold ${sort === 'price' ? 'text-white' : 'border border-border text-text-secondary'}`} style={sort === 'price' ? { backgroundColor: cfg.accent } : {}}>Lowest price</button>
            <button onClick={() => setSort('rating')} aria-pressed={sort==='rating'} data-testid="drawer-sort-rating" className={`px-2 py-1 rounded font-semibold ${sort === 'rating' ? 'text-white' : 'border border-border text-text-secondary'}`} style={sort === 'rating' ? { backgroundColor: cfg.accent } : {}}>Top rated</button>
          </div>
        </div>

        {sortedVendors.length === 0 ? (
          <Empty message="No vendors are offering this yet." />
        ) : (
          <div className="space-y-2">
            {sortedVendors.map((v) => {
              const reqDocs = v.required_documents || [];
              const ready = reqDocs.filter((d) => filledSlots.has(`${d.dataroom_id}::${d.slot_id}`)).length;
              const isExpanded = expandedVendor === v.vendor_id;
              const isCredit = category === 'credit';
              const ct = v.credit_terms || {};
              return (
                <div key={v.vendor_id} className="service-partner-card bg-surface border border-border rounded-lg p-3" data-testid={`drawer-vendor-row-${v.vendor_id}`}>
                  <div className="service-partner-summary flex items-center gap-3">
                    {isServiceFlow && <ServicePartnerLogo vendor={v} />}
                    <div className="service-partner-identity flex-1 min-w-0">
                      <div className="text-sm font-semibold text-text-primary truncate">{v.vendor_name}</div>
                      <div className="text-[11px] text-text-muted truncate">{v.vendor_tagline}</div>
                      {v.rating_avg != null && (
                        <div className="text-[10px] text-text-muted flex items-center gap-1 mt-0.5" data-testid={`drawer-rating-${v.vendor_id}`}>
                          <span style={{ color: '#f59e0b' }}>&#9733;</span>
                          <span className="font-mono">{Number(v.rating_avg).toFixed(1)}</span>
                          <span>&middot; {v.review_count} review{v.review_count === 1 ? '' : 's'}</span>
                        </div>
                      )}
                    </div>
                    {isCredit ? (
                      <div className="text-right text-[10px] text-text-muted leading-tight">
                        {(ct.ticket_size_min != null || ct.ticket_size_max != null) && (
                          <div className="font-mono text-xs text-text-primary">{format(ct.ticket_size_min || 0, 'INR')}–{format(ct.ticket_size_max || 0, 'INR')}</div>
                        )}
                        {(ct.interest_rate_min != null || ct.interest_rate_max != null) && (
                          <div>APR <span className="font-mono" style={{ color: cfg.accent }}>{ct.interest_rate_min || 0}–{ct.interest_rate_max || 0}%</span></div>
                        )}
                        {ct.approval_time && <div>Approval: {ct.approval_time}</div>}
                      </div>
                    ) : isServiceFlow ? (
                      <div className="service-partner-price text-right">
                        <div className="font-mono font-bold text-sm" style={{ color: cfg.accent }} data-testid={`drawer-total-${v.vendor_id}`}>
                          {format(v.total_price ?? v.gross_price ?? 0, v.currency || 'INR')}
                        </div>
                        <div className="text-[10px] text-text-muted leading-tight">
                          {format(v.gross_price || 0, v.currency || 'INR')} service
                          {(v.govt_fees || 0) > 0 && <> + {format(v.govt_fees, v.currency || 'INR')} govt</>}
                        </div>
                        {(v.delivery_days || v.turnaround_days) && (
                          <div className="text-[10px] text-text-muted">{v.delivery_days || v.turnaround_days}-day delivery</div>
                        )}
                      </div>
                    ) : (
                      <div className="text-right">
                        <div className="font-mono font-bold text-sm" style={{ color: cfg.accent }}>{format(v.gross_price || 0, v.currency || 'INR')}</div>
                        <div className="text-[10px] text-text-muted">{cadenceLabel(v.cadence)}{v.turnaround_days ? ` \u00b7 ${v.turnaround_days}d` : ''}</div>
                      </div>
                    )}
                    {isServiceFlow ? (
                      <button onClick={() => startRequest(v.vendor_id)} disabled={!!starting}
                        data-testid={`drawer-select-${v.vendor_id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg text-white hover:opacity-90 disabled:opacity-50"
                        style={{ backgroundColor: cfg.accent }}>
                        {starting === v.vendor_id ? 'Starting\u2026' : 'Choose partner'} {starting !== v.vendor_id && <ArrowRight size={9} weight="bold" />}
                      </button>
                    ) : (
                      <button onClick={() => onIntro({ id: v.vendor_id, name: v.vendor_name, catalog_offerings: [{ catalog_item_id: data.item.id, catalog_item_name: data.item.name, gross_price: v.gross_price, currency: v.currency }] })}
                        data-testid={`drawer-intro-${v.vendor_id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg text-white hover:opacity-90"
                        style={{ backgroundColor: cfg.accent }}>
                        Request intro <ArrowRight size={9} weight="bold" />
                      </button>
                    )}
                  </div>
                  {isCredit && (ct.collateral_type || ct.tenure_months_max || ct.repayment_frequency || ct.processing_fee_value != null) && (
                    <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                      {ct.tenure_months_max && <span className="px-1.5 py-0.5 rounded bg-background border border-border">Tenure: <span className="font-mono">{ct.tenure_months_min || 0}–{ct.tenure_months_max}m</span></span>}
                      {ct.collateral_type && <span className="px-1.5 py-0.5 rounded bg-background border border-border">{String(ct.collateral_type).replace(/_/g, ' ')}</span>}
                      {ct.repayment_frequency && <span className="px-1.5 py-0.5 rounded bg-background border border-border">{ct.repayment_frequency} repayment</span>}
                      {ct.processing_fee_value != null && <span className="px-1.5 py-0.5 rounded bg-background border border-border">Fee: <span className="font-mono">{ct.processing_fee_type === 'flat' ? format(ct.processing_fee_value, 'INR') : `${ct.processing_fee_value}%`}</span></span>}
                      {(ct.disbursement_days_min != null || ct.disbursement_days_max != null) && <span className="px-1.5 py-0.5 rounded bg-background border border-border">Disbursed in <span className="font-mono">{ct.disbursement_days_min || 0}–{ct.disbursement_days_max || 0}d</span></span>}
                    </div>
                  )}
                  {reqDocs.length > 0 && (
                    <button onClick={() => setExpandedVendor(isExpanded ? null : v.vendor_id)}
                      aria-expanded={isExpanded} aria-controls={`service-docs-${v.vendor_id}`} data-testid={`drawer-docs-toggle-${v.vendor_id}`}
                      className="mt-2 w-full text-[11px] inline-flex items-center justify-between px-2.5 py-1.5 rounded border border-border bg-background hover:border-text-primary">
                      <span className="inline-flex items-center gap-1.5 text-text-secondary">
                        <FileText size={11} weight="bold" style={{ color: cfg.accent }} />
                        {reqDocs.length} doc{reqDocs.length === 1 ? '' : 's'} required &middot; <strong style={{ color: ready === reqDocs.length ? cfg.accent : 'var(--text-secondary)' }}>{ready}/{reqDocs.length} ready</strong>
                      </span>
                      <CaretDown size={10} weight="bold" className="text-text-muted" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }} />
                    </button>
                  )}
                  {isExpanded && reqDocs.length > 0 && (
                    <div className="mt-2 p-3 bg-background rounded-lg border border-border" id={`service-docs-${v.vendor_id}`} data-testid={`drawer-docs-panel-${v.vendor_id}`}>
                      <RequiredDocsView items={reqDocs} filledSlots={filledSlots} taxonomy={taxonomy} accent={cfg.accent} compact />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const IntroModal = ({ category, cfg, vendor, onClose }) => {
  const { format } = useCurrency();
  const offerings = vendor.catalog_offerings || vendor.offerings || [];
  const [offeringId, setOfferingId] = useState(offerings[0]?.catalog_item_id || offerings[0]?.id || '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // (Iter 51) For SERVICES only — show a managed-engagement disclaimer first so
  // founders understand Founderboard owns the delivery + can't back-stop side deals.
  const [acknowledged, setAcknowledged] = useState(category !== 'service');

  const submit = async () => {
    setBusy(true);
    try {
      if (category === 'agency') {
        await api.post('/intro-calls', { agency_id: vendor.id, note });
      } else {
        await api.post('/vendor-intros', { vendor_id: vendor.id, offering_id: offeringId || null, note });
      }
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  // Services-only disclaimer card. Founder must check the acknowledgment before
  // the rest of the form unlocks.
  const showServicesDisclaimer = category === 'service' && !acknowledged && !done;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-start justify-center pt-16 p-4" onClick={onClose} data-testid="marketplace-intro-modal">
      <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-4">
          <h3 className="font-heading font-bold text-lg" style={{ letterSpacing: '-0.01em' }}>Request intro · {vendor.name}</h3>
          <button onClick={onClose}><X size={16} weight="bold" /></button>
        </div>
        {done ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 rounded-full mx-auto mb-3 inline-flex items-center justify-center" style={{ backgroundColor: `${cfg.accent}1a`, color: cfg.accent }}><Check size={20} weight="bold" /></div>
            <p className="text-sm text-text-secondary mb-4">Intro requested! {vendor.name} will reach you at your registered email within 48 hours.</p>
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: cfg.accent }}>Done</button>
          </div>
        ) : showServicesDisclaimer ? (
          <div className="space-y-4" data-testid="marketplace-intro-services-disclaimer">
            <div className="rounded-xl p-4" style={{ backgroundColor: `${cfg.accent}12`, border: `1px solid ${cfg.accent}55` }}>
              <div className="flex items-start gap-2.5 mb-2">
                <ShieldCheck size={18} weight="fill" style={{ color: cfg.accent }} className="flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-heading font-bold text-text-primary leading-tight">Founderboard owns this delivery.</div>
                  <p className="text-[12px] text-text-secondary mt-1 leading-relaxed">
                    Our team manages every step end-to-end &mdash; vendor selection, timelines, payments, escalations and quality.
                    {vendor.name} is one of our vetted service providers, and we&rsquo;ll keep you on track with milestones, SLAs and a single contract.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-xl p-4 border border-warning/40 bg-warning/5">
              <div className="flex items-start gap-2.5">
                <Warning size={16} weight="fill" className="text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-[12px] font-semibold text-text-primary">If you engage outside Founderboard, you&rsquo;re on your own.</div>
                  <p className="text-[11px] text-text-secondary mt-1 leading-relaxed">
                    Founderboard cannot offer SLAs, dispute mediation, escrowed payments, refunds, quality guarantees, or any liability cover for work agreed off-platform.
                    Anything contracted directly with {vendor.name} (or any other provider) sits entirely between you and them.
                  </p>
                </div>
              </div>
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-lg border border-border hover:border-text-primary" data-testid="marketplace-intro-ack-label">
              <input type="checkbox" className="mt-0.5" data-testid="marketplace-intro-ack-checkbox"
                onChange={(e) => setAcknowledged(e.target.checked)} />
              <span className="text-[12px] text-text-secondary leading-relaxed">
                I understand Founderboard manages this engagement and I&rsquo;ll keep the relationship on-platform.
              </span>
            </label>
          </div>
        ) : (
          <>
            {category !== 'agency' && offerings.length > 0 && (
              <div className="mb-3">
                <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Interested in</label>
                <select value={offeringId} onChange={(e) => setOfferingId(e.target.value)} data-testid="marketplace-intro-offering" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm">
                  <option value="">No specific offering</option>
                  {offerings.map((o, i) => <option key={o.catalog_item_id || o.id || i} value={o.catalog_item_id || o.id}>{o.catalog_item_name || o.title} — {format(o.gross_price || 0, o.currency || 'INR')}</option>)}
                </select>
              </div>
            )}
            <div className="mb-4">
              <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Anything specific to share?</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="marketplace-intro-note" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" placeholder="e.g. ‘Need GST + LLP done in 10 days’" />
            </div>
            <button onClick={submit} disabled={busy} data-testid="marketplace-intro-submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: cfg.accent }}>
              <PaperPlaneTilt size={11} weight="bold" /> {busy ? 'Sending…' : 'Send request'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default MarketplaceShell;
